import type { CellRect, CellRefusal, DraftGridLens } from "@heraion/shared";

import type { TFn } from "../i18nTypes";
import type { SheetMenuItem } from "../primitives/SheetMenu";

// The wide grid's range actions as a menu (#336 slice 6 part 3, brief §7.3):
// what the keys do, reachable by a finger. Copy works in every lens; the
// others write, so only the Sets lens takes them. An item the write path would
// refuse is disabled in advance, and the refusal says why under the label (the
// words are useRoundCellWrites' own, passed in as `why`).
export const RANGE_ACTIONS = ["copy", "paste", "fillDown", "fillRight"] as const;
export type RangeAction = (typeof RANGE_ACTIONS)[number];

/** The keys that do the same, shown where a keyboard is. */
const RANGE_SHORTCUT: Record<RangeAction, string> = {
  copy: "Ctrl+C",
  paste: "Ctrl+V",
  fillDown: "Ctrl+D",
  fillRight: "Ctrl+R",
};

/** Why an action is refused now, or null when it is allowed. Shared with the
 *  cell's screen-reader actions, which offer only what is allowed. */
export function rangeRefusal({
  t,
  why,
  action,
  lens,
  rect,
  canRead,
}: {
  t: TFn;
  why: (r: CellRefusal) => string;
  action: RangeAction;
  lens: DraftGridLens;
  rect: CellRect;
  canRead: boolean;
}): string | null {
  if (action === "copy") return null;
  if (lens !== "sets") return why("setsOnly");
  if (action === "paste" && !canRead) {
    return t("editorV3.grid.menu.noClipboardRead", { defaultValue: "this browser does not let the page read the clipboard" });
  }
  if (action === "fillDown" && rect.top === rect.bottom) {
    return t("editorV3.grid.menu.needsRows", { defaultValue: "select more than one row" });
  }
  if (action === "fillRight" && rect.left === rect.right) {
    return t("editorV3.grid.menu.needsCols", { defaultValue: "select more than one round" });
  }
  return null;
}

export function gridRangeMenuItems({
  t,
  why,
  lens,
  rect,
  canRead,
  run,
}: {
  t: TFn;
  why: (r: CellRefusal) => string;
  lens: DraftGridLens;
  rect: CellRect;
  canRead: boolean;
  run: Record<RangeAction, () => void>;
}): SheetMenuItem[] {
  const labels: Record<RangeAction, { label: string; icon: SheetMenuItem["icon"] }> = {
    copy: { label: t("editorV3.grid.menu.copy", { defaultValue: "Copy" }), icon: "copy" },
    paste: { label: t("editorV3.grid.menu.paste", { defaultValue: "Paste" }), icon: "inbox" },
    fillDown: { label: t("editorV3.grid.menu.fillDown", { defaultValue: "Fill down" }), icon: "chevron-down" },
    fillRight: { label: t("editorV3.grid.menu.fillRight", { defaultValue: "Fill right" }), icon: "arrow-right" },
  };
  return RANGE_ACTIONS.map((action) => {
    const hint = rangeRefusal({ t, why, action, lens, rect, canRead }) ?? undefined;
    return {
      key: action,
      ...labels[action],
      onPress: run[action],
      disabled: hint !== undefined,
      hint,
      shortcut: RANGE_SHORTCUT[action],
    };
  });
}
